import { ErrorHandler, importProvidersFrom, ModuleWithProviders, NgModule, Optional, provideAppInitializer, SkipSelf } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
// TODO-EL: uncomment the following line to enable client side hydration
// import { provideClientHydration, withIncrementalHydration } from '@angular/platform-browser';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { CurrencyPipe, IMAGE_CONFIG } from '@angular/common';
import { OAuthService } from 'angular-oauth2-oidc';
import { WindowRef } from 'ngx-tag-commander';

import {
  AuthConfigService, AuthGuard, AuthHttpHeaderService, AuthService, BadRequestHandler, BasePageMetaResolver,
  CategoryPageMetaResolver, ClientAuthenticationTokenService, HttpErrorHandler, OAuthLibWrapperService,
  OccCmsPageNormalizer, PRODUCT_NORMALIZER, ProductImageNormalizer, ProductNameNormalizer,
  ProductPageMetaResolver, ProductReferenceNormalizer, SiteContextInterceptor,
  CmsComponentConnector, PageLinkService
} from '@spartacus/core';

import { AppRoutingModule, CmsPageGuard, ProductListComponentService } from '@spartacus/storefront';
import { SmartEditLauncherService } from '@spartacus/smartedit/root';
import { ActiveCartService } from '@spartacus/cart/base/core';
import { OccCartNormalizer } from '@spartacus/cart/base/occ';
import { OccOrderNormalizer } from '@spartacus/order/occ';

import { BrandsEnum } from '@seb/web-design-system/core';

import {
  CustomActiveCartService, CustomAuthConfigService, CustomAuthHttpHeaderService, CustomAuthService, CustomCartNormalizer,
  CustomClientAuthenticationTokenService, CustomOAuthLibWrapperService, CustomOAuthService, CustomOrderNormalizer,
  CustomPageLinkService, CustomPageNormalizer, CustomProductImageNormalizer, CustomProductListComponentService,
  CustomProductNameNormalizer, CustomProductReferenceNormalizer, CustomSmartEditLauncherService
} from './services';

import { smartEditTimestampInterceptor } from './interceptors/smart-edit-timestamp.interceptor';
import { CustomSiteContextInterceptor } from './interceptors/custom-site-context.interceptor';
import { InternalServerErrorHandler } from './interceptors/internal-server-error.handler';
import { ExpressCheckoutInterceptor } from './interceptors/express-checkout.interceptor';
import { CustomBadRequestHandler } from './interceptors/custom-bad-request.handler';
import { MaintenanceErrorHandler } from './interceptors/maintenance-error.handler';
import { WebCommonErrorHandler } from './interceptors/web-common-error.handler';
import { kwankoAwinInterceptor } from './interceptors/kwanko-awin.interceptor';

import { CustomCategoryPageMetaResolver } from './resolvers/custom-category-page-meta.resolver';
import { CustomProductPageMetaResolver } from './resolvers//custom-product-page-meta.resolver';
import { CustomBasePageMetaResolver } from './resolvers/custom-base-page-meta.resolver';

import { CustomCmsPageGuard } from './guards/custom-cms-page.guard';
import { CustomAuthGuard } from './guards/custom-auth.guard';

import { CustomCmsComponentConnector } from './connectors/custom-cms-component.connector';
import { WC_CONFIG_PROVIDER } from './models/wc-config.model';

import { appInitDefaultLang, appInitLang } from './app-initializers/i18n.app-initializer';
import { appMainInitializer } from './app-initializers/base-site.app-initializer';
import { appInitDatalayer } from './app-initializers/datalayer.app-initializer';

import { commonRoutes, accountRoutes, cartRoutes, catalogRoutes, recipeRoutes } from './web-common.routes';
import { SpartacusModule } from './spartacus/spartacus.module';
import { getBrandConfig } from './web-common.config';

const appRoutes = [
  ...commonRoutes,
  ...accountRoutes,
  ...cartRoutes,
  ...catalogRoutes,
  ...recipeRoutes
];

@NgModule({
  imports: [AppRoutingModule]
})
export class WebCommonModule {
  static appBrand: BrandsEnum;

  constructor(@Optional() @SkipSelf() parent: WebCommonModule) {
    if (parent) {
      throw new Error('WebCommonModule cannot be loaded twice!');
    }
  }

  static withBrand(brand: BrandsEnum): ModuleWithProviders<WebCommonModule> {
    // Store the global application base brand
    WebCommonModule.appBrand = brand;

    return {
      ngModule: WebCommonModule,
      providers: [
        WindowRef,
        CurrencyPipe,

        provideRouter(
          appRoutes,
          withRouterConfig({
            onSameUrlNavigation: 'reload'
          }),
          withInMemoryScrolling({
            scrollPositionRestoration: 'disabled',
            anchorScrolling: 'disabled'
          })
        ),

        { provide: PRODUCT_NORMALIZER, useExisting: CustomProductReferenceNormalizer, multi: true },
        { provide: IMAGE_CONFIG, useValue: { disableImageSizeWarning: true } },
        { provide: WC_CONFIG_PROVIDER, useValue: getBrandConfig(brand) },

        { provide: BadRequestHandler, useExisting: CustomBadRequestHandler },
        { provide: ProductPageMetaResolver, useExisting: CustomProductPageMetaResolver },
        { provide: BasePageMetaResolver, useExisting: CustomBasePageMetaResolver },
        { provide: ProductListComponentService, useExisting: CustomProductListComponentService },
        { provide: SiteContextInterceptor, useExisting: CustomSiteContextInterceptor },
        { provide: HttpErrorHandler, useExisting: InternalServerErrorHandler, multi: true },
        { provide: HttpErrorHandler, useExisting: MaintenanceErrorHandler, multi: true },
        { provide: OccCmsPageNormalizer, useExisting: CustomPageNormalizer },
        { provide: OAuthLibWrapperService, useExisting: CustomOAuthLibWrapperService },
        { provide: AuthService, useExisting: CustomAuthService },
        { provide: OAuthService, useExisting: CustomOAuthService },
        { provide: AuthConfigService, useExisting: CustomAuthConfigService },
        { provide: ProductImageNormalizer, useExisting: CustomProductImageNormalizer },
        { provide: ProductNameNormalizer, useExisting: CustomProductNameNormalizer },
        { provide: ProductReferenceNormalizer, useExisting: CustomProductReferenceNormalizer },
        { provide: CmsPageGuard, useExisting: CustomCmsPageGuard },
        { provide: OccCartNormalizer, useExisting: CustomCartNormalizer },
        { provide: OccOrderNormalizer, useExisting: CustomOrderNormalizer },
        { provide: AuthHttpHeaderService, useExisting: CustomAuthHttpHeaderService },
        { provide: ClientAuthenticationTokenService, useExisting: CustomClientAuthenticationTokenService },
        { provide: CategoryPageMetaResolver, useExisting: CustomCategoryPageMetaResolver },
        { provide: AuthGuard, useExisting: CustomAuthGuard },
        { provide: ActiveCartService, useExisting: CustomActiveCartService },
        { provide: SmartEditLauncherService, useExisting: CustomSmartEditLauncherService },
        { provide: CmsComponentConnector, useExisting: CustomCmsComponentConnector },
        { provide: PageLinkService, useExisting: CustomPageLinkService },
        { provide: ErrorHandler, useExisting: WebCommonErrorHandler },
        { provide: HTTP_INTERCEPTORS, useClass: ExpressCheckoutInterceptor, multi: true },

        importProvidersFrom(
          SpartacusModule.withBrand(brand)
        ),

        // TODO-EL: uncomment the following lines to enable client side hydration
        // provideClientHydration(
        //   withIncrementalHydration()
        // ),

        provideHttpClient(
          withFetch(),
          withInterceptorsFromDi(),
          withInterceptors([
            smartEditTimestampInterceptor,
            kwankoAwinInterceptor
          ])
        ),

        provideAppInitializer(appMainInitializer),
        provideAppInitializer(appInitDatalayer),
        provideAppInitializer(appInitDefaultLang),
        provideAppInitializer(appInitLang)
      ]
    };
  }
}
